<template>
  <div class="app-update-notifications">
    <!-- Show update notifications if any exist -->
    <template v-if="updateNotifications.length > 0">
      <b-dropdown-item 
        v-for="notification in updateNotifications" 
        :key="notification.id"
        @click="handleNotificationClick(notification)"
        class="update-notification-item"
      >
        <b-media class="app-update">
          <template #aside>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="feather feather-download-cloud"
              :class="{ 'text-success': notification.type === 'updated', 'text-info': notification.type === 'available' }"
            >
              <polyline points="8 17 12 21 16 17"></polyline>
              <line x1="12" y1="12" x2="12" y2="21"></line>
              <path d="M20.88 18.09A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.29"></path>
            </svg>
          </template>
          <div class="data-info">
            <h6 class="">{{ notification.title }}</h6>
            <p class="">{{ notification.timeAgo }}</p>
          </div>

          <div class="icon-status">
            <svg
              v-if="notification.type === 'updated'"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="feather feather-check text-success"
            >
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            <svg
              v-else
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="feather feather-download text-info"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
          </div>
        </b-media>
      </b-dropdown-item>
      
      <!-- Divider if there are update notifications -->
      <b-dropdown-divider></b-dropdown-divider>
    </template>

    <!-- Empty state when no updates -->
    <template v-else-if="showEmptyState">
      <b-dropdown-item disabled class="text-center text-muted">
        <small>No app updates</small>
      </b-dropdown-item>
      <b-dropdown-divider></b-dropdown-divider>
    </template>
  </div>
</template>

<script>
import updateNotificationService from '@/services/updateNotificationService';

export default {
  name: 'AppUpdateNotifications',
  props: {
    showEmptyState: {
      type: Boolean,
      default: false
    },
    maxNotifications: {
      type: Number,
      default: 3
    }
  },
  data() {
    return {
      updateNotifications: [],
      currentVersion: null
    };
  },
  mounted() {
    this.initializeWithService();
    
    // Expose test method globally for easy testing
    if (process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost') {
      window.testAppUpdateNotification = this.testNotification;
      console.log('🧪 Test method available: window.testAppUpdateNotification()');
    }
  },
  beforeDestroy() {
    this.cleanup();
  },
  methods: {
    initializeWithService() {
      try {
        // Subscribe to the update notification service
        this.unsubscribeUpdates = updateNotificationService.subscribe((data) => {
          this.updateNotifications = data.notifications
            .slice(0, this.maxNotifications)
            .map(n => ({
              ...n,
              title: this.getNotificationTitle(n),
              timeAgo: this.formatTimeAgo(n.timestamp)
            }));
        });

        // Get initial notifications
        this.updateNotifications = updateNotificationService.getUpdateNotifications()
          .slice(0, this.maxNotifications)
          .map(n => ({
            ...n,
            title: this.getNotificationTitle(n),
            timeAgo: this.formatTimeAgo(n.timestamp)
          }));

        this.currentVersion = process.env.VUE_APP_VERSION;
        
        console.log('🔔 AppUpdateNotifications initialized with service');
      } catch (error) {
        console.error('Error initializing update notifications:', error);
        this.updateNotifications = [];
      }
    },

    getNotificationTitle(notification) {
      switch (notification.type) {
        case 'updated':
          return `✅ Updated to v${notification.version}`;
        case 'available':
          return `📱 Update Available: v${notification.version}`;
        case 'ready':
          return `🚀 Update Ready: v${notification.version}`;
        case 'broadcast':
          return `📡 New Version: v${notification.version}`;
        default:
          return `App Update: v${notification.version || 'New Version'}`;
      }
    },

    setupUpdateListeners() {
      // Listen for service worker update events
      window.addEventListener('sw-updated', this.handleServiceWorkerUpdate);
      window.addEventListener('sw-version-ready', this.handleVersionReady);
      
      // Listen for update available events (from registerServiceWorker.js)
      window.addEventListener('app-update-available', this.handleUpdateAvailable);
      window.addEventListener('app-update-downloaded', this.handleUpdateDownloaded);
      
      // Update time display every minute
      this.timeUpdateInterval = setInterval(() => {
        this.updateTimeAgo();
      }, 60000);
    },

    handleServiceWorkerUpdate(event) {
      const { version, buildTime, isUpdate } = event.detail;
      
      if (isUpdate && version !== this.currentVersion) {
        this.addNotification({
          type: 'updated',
          title: `App Updated to v${version}`,
          version: version,
          buildTime: buildTime,
          timestamp: Date.now()
        });
        
        this.currentVersion = version;
      }
    },

    handleVersionReady(event) {
      const { version } = event.detail;
      this.currentVersion = version;
    },

    handleUpdateAvailable(event) {
      const { version } = event.detail || {};
      this.addNotification({
        type: 'available',
        title: `Update Available: v${version || 'New Version'}`,
        version: version,
        timestamp: Date.now()
      });
    },

    handleUpdateDownloaded(event) {
      const { version } = event.detail || {};
      this.addNotification({
        type: 'downloaded',
        title: `Update Ready: v${version || 'New Version'}`,
        version: version,
        timestamp: Date.now()
      });
    },

    addNotification(notification) {
      // Create unique ID
      notification.id = `update_${notification.timestamp}_${Math.random().toString(36).substr(2, 9)}`;
      notification.timeAgo = this.formatTimeAgo(notification.timestamp);
      
      // Remove any existing notification for the same version
      this.updateNotifications = this.updateNotifications.filter(n => n.version !== notification.version);
      
      // Add new notification at the beginning
      this.updateNotifications.unshift(notification);
      
      // Limit notifications
      if (this.updateNotifications.length > this.maxNotifications) {
        this.updateNotifications = this.updateNotifications.slice(0, this.maxNotifications);
      }
      
      // Save to localStorage
      this.saveNotifications();
      
      console.log('📱 App update notification added:', notification);
    },

    handleNotificationClick(notification) {
      console.log('Update notification clicked:', notification);
      
      // Mark as read using the service
      updateNotificationService.markAsRead(notification.id);
      
      if (notification.type === 'available' || notification.type === 'ready' || notification.type === 'broadcast') {
        this.showUpdateDialog(notification);
      } else if (notification.type === 'updated') {
        this.showVersionInfo(notification);
      }
    },

    showUpdateDialog(notification) {
      if (this.$swal) {
        this.$swal({
          title: `🚀 ${notification.version}`,
          text: notification.message || 'A new version is available. Update now?',
          icon: 'success',
          showCancelButton: true,
          confirmButtonText: 'Update Now',
          cancelButtonText: 'Later',
          confirmButtonColor: '#4CAF50'
        }).then((result) => {
          if (result.isConfirmed) {
            updateNotificationService.refreshApp();
          }
        });
      } else {
        const shouldUpdate = confirm(`${notification.message}\n\nUpdate now?`);
        if (shouldUpdate) {
          updateNotificationService.refreshApp();
        }
      }
    },

    triggerAppUpdate() {
      // Use the service method for consistency
      updateNotificationService.refreshApp();
    },

    showVersionInfo(notification) {
      const versionInfo = {
        version: notification.version || 'Unknown',
        buildTime: notification.buildTime ? new Date(notification.buildTime).toLocaleString() : 'Unknown',
        updateTime: new Date(notification.timestamp).toLocaleString()
      };
      
      if (this.$swal) {
        this.$swal({
          title: 'App Update Info',
          html: `
            <div style="text-align: left; font-family: monospace; font-size: 12px;">
              <p><strong>Version:</strong> ${versionInfo.version}</p>
              <p><strong>Built:</strong> ${versionInfo.buildTime}</p>
              <p><strong>Updated:</strong> ${versionInfo.updateTime}</p>
            </div>
          `,
          icon: 'info',
          confirmButtonText: 'OK'
        });
      } else {
        alert(`App Version: ${versionInfo.version}\nUpdated: ${versionInfo.updateTime}`);
      }
    },

    updateTimeAgo() {
      this.updateNotifications.forEach(notification => {
        notification.timeAgo = this.formatTimeAgo(notification.timestamp);
      });
    },

    formatTimeAgo(timestamp) {
      const now = Date.now();
      const diff = now - timestamp;
      const minutes = Math.floor(diff / (1000 * 60));
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));

      if (minutes < 1) {
        return 'Just now';
      } else if (minutes < 60) {
        return `${minutes} min ago`;
      } else if (hours < 24) {
        return `${hours} hour${hours > 1 ? 's' : ''} ago`;
      } else {
        return `${days} day${days > 1 ? 's' : ''} ago`;
      }
    },

    isNotificationValid(notification) {
      // Check if notification is not too old (7 days)
      const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
      return notification.timestamp > sevenDaysAgo;
    },

    cleanupOldNotifications() {
      try {
        const stored = localStorage.getItem('app_update_notifications');
        if (stored) {
          const notifications = JSON.parse(stored);
          const validNotifications = notifications.filter(n => this.isNotificationValid(n));
          
          if (validNotifications.length !== notifications.length) {
            localStorage.setItem('app_update_notifications', JSON.stringify(validNotifications));
          }
        }
      } catch (error) {
        console.error('Error cleaning up notifications:', error);
      }
    },

    saveNotifications() {
      try {
        localStorage.setItem('app_update_notifications', JSON.stringify(this.updateNotifications));
      } catch (error) {
        console.error('Error saving notifications:', error);
      }
    },

    cleanup() {
      if (this.timeUpdateInterval) {
        clearInterval(this.timeUpdateInterval);
      }
      
      if (this.unsubscribeUpdates) {
        this.unsubscribeUpdates();
      }
    },

    // Test method - can be called from browser console for testing
    testNotification(type = 'updated') {
      const testVersion = `1.3.8-test${Math.random().toString(36).substr(2, 5)}`;
      
      if (type === 'available') {
        this.handleUpdateAvailable({ detail: { version: testVersion } });
      } else if (type === 'downloaded') {
        this.handleUpdateDownloaded({ detail: { version: testVersion } });
      } else {
        this.handleServiceWorkerUpdate({ 
          detail: { 
            version: testVersion, 
            buildTime: new Date().toISOString(),
            isUpdate: true 
          } 
        });
      }
      
      console.log(`🧪 Test notification created: ${type} - ${testVersion}`);
    }
  }
};
</script>

<style scoped>
.update-notification-item {
  cursor: pointer;
}

.update-notification-item:hover {
  background-color: rgba(0, 123, 255, 0.1);
}

.app-update .data-info h6 {
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 2px;
}

.app-update .data-info p {
  font-size: 11px;
  color: #888;
  margin-bottom: 0;
}

.text-success {
  color: #28a745 !important;
}

.text-info {
  color: #17a2b8 !important;
}

.text-muted {
  color: #6c757d !important;
}
</style> 